/**
 * Ponte entre o formulário e o domínio.
 *
 * Concentra a tradução dos valores da interface para o documento de proposta, o cálculo do preço e a
 * renderização do preview. É camada pura de propósito: pode ser testada sem React, sem DOM e sem
 * navegador, o que é onde a maior parte dos defeitos de um formulário complexo aparece.
 *
 * A regra que organiza tudo: **o formulário não calcula nada**. Ele coleta premissas, o domínio
 * calcula, e o resultado alimenta o documento e o preview.
 */

import {
  exigePtax,
  precificar,
  sincronizacaoInicial,
  validarDocumento,
  VERSAO_SCHEMA_ATUAL,
  type DocumentoProposta,
  type EntradaPrecificacao,
  type ItemCusto,
  type JanelaCreditoMeses,
  type ModalidadeDeploy,
  type EntradaSafeStorage,
  type ItemCustoAdicional,
  type FocoAssessment,
  type OportunidadeCrm,
  type PlanoSafeStorage,
  type PlanoSuporte,
  type ResponsavelCongelado,
  type ResultadoPrecificacao,
  type Trilha,
} from '@emaster/dominio';
import { renderizarProposta, VERSAO_TEMPLATE } from '@emaster/template-proposta';

// ---------------------------------------------------------------------------
// Valores do formulário
// ---------------------------------------------------------------------------

/**
 * Forma achatada dos campos, próxima do que a interface manipula.
 *
 * Números são mantidos como `number` e não como texto: a conversão acontece na borda do campo, para
 * que o restante do fluxo não precise lidar com string vazia nem com vírgula decimal.
 */
/** Opções do seletor de assessment. `ambos` é combinação de tela, e não um foco do domínio. */
export type EscolhaAssessment = FocoAssessment | 'ambos';

export interface ValoresFormulario {
  /** Número da oportunidade ativa escolhida no Dynamics; exigido pelo comando de publicar. */
  readonly oportunidadeNumero: string;
  /**
   * Dados da oportunidade selecionada no CRM. Pode ser nulo enquanto o formulário é preenchido, mas
   * Publicar/Republicar exige o objeto com GUID da oportunidade e GUID da conta para criar a Cotação
   * vinculada no Dynamics.
   */
  readonly oportunidade: OportunidadeCrm | null;

  readonly trilha: Trilha;
  readonly cliente: string;
  /**
   * GUID (accountid) da conta selecionada no Dynamics. Estado de UI, não entra no documento (o
   * vínculo forte viaja em `oportunidade.contaId`); serve para listar as oportunidades ativas do
   * cliente escolhido. `null` enquanto nenhum cliente foi selecionado da lista.
   */
  readonly clienteContaId: string | null;
  readonly projeto: string;
  readonly validaAte: string;

  readonly planoSuporte: PlanoSuporte;
  readonly duracaoContratoMeses: 12 | 36;

  readonly ptax: number | null;
  readonly ptaxOrigem: 'api' | 'manual' | 'nao_aplicavel';

  readonly itensCusto: readonly ItemCusto[] | null;
  readonly regiaoAws: string | null;
  readonly consumoManual: number | null;

  readonly incluiCredito: boolean;
  readonly creditoValorUsd: number;
  readonly creditoJanelaMeses: JanelaCreditoMeses;

  readonly modalidadeDeploy: ModalidadeDeploy;
  readonly deployCusto: number;

  readonly incluiBackup: boolean;
  readonly backupFileServer: number;
  readonly backupVirtualServer: number;
  readonly backupSharepoint: number;
  readonly backupVmwareHyperv: number;

  readonly incluiSafeStorage: boolean;
  readonly safestoragePlano: PlanoSafeStorage;
  readonly safestorageUsuarios: number;
  /** Mensalidade negociada em USD. Usada apenas na faixa Enterprise, que é sob consulta. */
  readonly safestorageMensalidadeUsd: number | null;

  readonly incluiCustosAdicionais: boolean;
  /**
   * Linhas de custo adicional como o vendedor as digita, na moeda de entrada da trilha.
   *
   * Sobrevive à desmarcação do seletor, como `sessoesTreinamento`: quem desmarca por engano e marca de
   * novo não perde o que escreveu. É `montarDocumento` que aplica a desmarcação, gravando `null`.
   *
   * A lista pode conter linhas em branco enquanto o vendedor digita — uma linha recém-adicionada nasce
   * vazia. O descarte acontece na tradução para o domínio, não aqui.
   */
  readonly custosAdicionais: readonly ItemCustoAdicional[];

  readonly incluiAssessment: boolean;
  /**
   * Escolha de tela do assessment. `ambos` é afordância da interface, não do domínio: o documento
   * guarda a lista de focos, e é `montarAssessment` que traduz.
   */
  readonly assessmentEscolha: EscolhaAssessment;

  readonly incluiTreinamento: boolean;
  /**
   * Quantidade de sessões escolhida. O documento guarda zero quando o marcador está desmarcado, e é
   * `montarDocumento` que aplica isso — aqui o número sobrevive à desmarcação, para o vendedor não
   * perder a escolha ao alternar o marcador.
   */
  readonly sessoesTreinamento: number;

  // Conteúdo editorial
  readonly badgeContexto: string;
  readonly promessaValor: string;
  readonly subtituloHero: string;
  readonly observacoesHtml: string;

  // Desenho de arquitetura (data URL da imagem, ou string vazia)
  readonly arquiteturaDataUrl: string;
}

/** Data de hoje em `AAAA-MM-DD`, em UTC. */
function hojeUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Soma dias a uma data `AAAA-MM-DD`, em UTC. */
export function somarDias(data: string, dias: number): string {
  const [ano, mes, dia] = data.split('-').map(Number) as [number, number, number];
  const d = new Date(Date.UTC(ano, mes - 1, dia + dias));
  return d.toISOString().slice(0, 10);
}

/**
 * Valores iniciais.
 *
 * Validade padrão de 30 dias. O legado usava 7 dias, prazo curto o bastante para que boa parte das
 * propostas vencesse antes de o cliente responder — e, como a proposta vencida ficava inacessível,
 * isso destruía o material de referência. As propostas customizadas do acervo usam 30 dias.
 */
export function valoresIniciais(trilha: Trilha = 'CS'): ValoresFormulario {
  const hoje = hojeUtc();

  return {
    oportunidadeNumero: '',
    oportunidade: null,

    trilha,
    cliente: '',
    clienteContaId: null,
    projeto: '',
    validaAte: somarDias(hoje, 30),

    planoSuporte: trilha === 'PS' ? 'enterprise' : 'business',
    duracaoContratoMeses: 12,

    ptax: null,
    ptaxOrigem: trilha === 'PS' ? 'api' : 'nao_aplicavel',

    itensCusto: null,
    regiaoAws: null,
    consumoManual: null,

    incluiCredito: false,
    creditoValorUsd: 0,
    creditoJanelaMeses: 3,

    modalidadeDeploy: 'nao_incluido',
    deployCusto: 0,

    incluiBackup: false,
    backupFileServer: 0,
    backupVirtualServer: 0,
    backupSharepoint: 0,
    backupVmwareHyperv: 0,

    incluiSafeStorage: false,
    safestoragePlano: 'orbita',
    safestorageUsuarios: 10,
    safestorageMensalidadeUsd: null,

    incluiCustosAdicionais: false,
    /* Uma linha em branco já pronta: marcar o seletor e não ter onde digitar é atrito sem motivo. */
    custosAdicionais: [{ descricao: '', valorMensal: 0 }],

    incluiAssessment: false,
    assessmentEscolha: 'ambos',

    incluiTreinamento: false,
    sessoesTreinamento: 1,

    badgeContexto: '',
    promessaValor: '',
    subtituloHero: '',
    observacoesHtml: '',

    arquiteturaDataUrl: '',
  };
}

// ---------------------------------------------------------------------------
// Tradução para o domínio
// ---------------------------------------------------------------------------

/** `true` quando há itens de CSV importados, o que ativa a aplicação da margem. */
export function temCsv(v: ValoresFormulario): boolean {
  return v.itensCusto !== null && v.itensCusto.length > 0;
}

/**
 * Custo de deploy sugerido: um mês de consumo.
 *
 * Regra do legado, preservada. A interface deve parar de sugerir assim que o vendedor editar o campo,
 * para não sobrescrever a decisão dele.
 */
export function deploySugerido(consumoNaMoedaDeEntrada: number): number {
  return Math.round(consumoNaMoedaDeEntrada * 100) / 100;
}

/**
 * Assinatura do SafeStorage no formato do domínio, ou `null` quando o produto não foi marcado.
 *
 * A mensalidade negociada só é repassada na faixa Enterprise. Nas faixas tabeladas ela é descartada
 * aqui, e não no motor: assim o número que sobrou de uma troca de plano não viaja dentro do documento
 * como premissa que ninguém aplicou.
 */
/**
 * Custos adicionais no formato do domínio, ou `null` quando não há nenhum aproveitável.
 *
 * Descarta linha sem descrição. A linha em branco existe porque o formulário oferece uma pronta para
 * digitar, e porque o botão de adicionar cria outra vazia — se ela chegasse ao motor, a prévia
 * quebraria com `CUSTO_ADICIONAL_SEM_DESCRICAO` no meio do preenchimento, antes de o vendedor ter tido
 * a chance de escrever o nome. O bloqueio tem de acontecer na publicação, não a cada tecla.
 *
 * Linha com nome e valor zero é **mantida**: serviço incluído sem custo é informação deliberada, e o
 * schema aceita zero.
 */
function montarCustosAdicionais(v: ValoresFormulario): readonly ItemCustoAdicional[] | null {
  if (!v.incluiCustosAdicionais) return null;

  const aproveitaveis = v.custosAdicionais
    .filter((i) => i.descricao.trim() !== '')
    .map((i) => ({ descricao: i.descricao.trim(), valorMensal: i.valorMensal }));

  return aproveitaveis.length > 0 ? aproveitaveis : null;
}

function montarSafeStorage(v: ValoresFormulario): EntradaSafeStorage | null {
  if (!v.incluiSafeStorage) return null;

  return {
    plano: v.safestoragePlano,
    usuarios: v.safestorageUsuarios,
    mensalidadeCustomizadaUsd:
      v.safestoragePlano === 'enterprise' ? v.safestorageMensalidadeUsd : null,
  };
}

/**
 * Focos do assessment no formato do documento, ou `null` quando a opção não está marcada.
 *
 * Traduz a escolha de tela para a lista que o documento guarda. `ambos` existe só aqui: manter a
 * combinação como afordância da interface é o que impede cada renderizador de reimplementar a
 * expansão por conta própria.
 */
function montarAssessment(v: ValoresFormulario): readonly FocoAssessment[] | null {
  if (!v.incluiAssessment) return null;
  if (v.assessmentEscolha === 'ambos') return ['custos', 'seguranca'];
  return [v.assessmentEscolha];
}

export function montarEntradaPrecificacao(v: ValoresFormulario): EntradaPrecificacao {
  const backup = v.incluiBackup
    ? {
        fileServer: v.backupFileServer,
        virtualServer: v.backupVirtualServer,
        sharepoint: v.backupSharepoint,
        vmwareHyperv: v.backupVmwareHyperv,
      }
    : null;

  return {
    trilha: v.trilha,
    duracaoContratoMeses: v.duracaoContratoMeses,
    planoSuporte: v.planoSuporte,
    ptax: v.ptax,
    itensCusto: temCsv(v) ? v.itensCusto : null,
    consumoManual: temCsv(v) ? null : v.consumoManual,
    credito: v.incluiCredito
      ? { valorUsd: v.creditoValorUsd, janelaMeses: v.creditoJanelaMeses }
      : null,
    deploy: { modalidade: v.modalidadeDeploy, custo: v.deployCusto },
    backup: backup,
    safestorage: montarSafeStorage(v),
    custosAdicionais: montarCustosAdicionais(v),
  };
}

/**
 * `true` quando o formulário precisa oferecer o campo de cotação do dólar.
 *
 * Delega a decisão a `exigePtax`, do domínio, em vez de repetir a condição aqui. Repetir foi o que
 * produziu o defeito: o painel condicionava o campo a `trilha === 'PS'` enquanto o motor exigia PTAX
 * também em CS por causa das licenças de backup, que têm preço de tabela em reais. O vendedor de CS
 * que marcasse backup ficava sem prévia e sem publicar, lendo uma mensagem sobre um campo que a
 * interface não mostrava.
 */
export function precisaCotacaoDolar(v: ValoresFormulario): boolean {
  return exigePtax(montarEntradaPrecificacao(v));
}

/**
 * Monta o documento a partir dos valores, do responsável e dos resultados calculados.
 *
 * O número da proposta é provisório enquanto ela é rascunho. A numeração definitiva e sequencial é
 * atribuída pelo servidor na publicação (Task 15), porque um contador no cliente não sobrevive a dois
 * vendedores publicando ao mesmo tempo.
 */
export function montarDocumento(
  v: ValoresFormulario,
  responsavel: ResponsavelCongelado,
  resultados: ResultadoPrecificacao,
  opcoes: { readonly numero?: string; readonly emitidaEm?: string } = {},
): unknown {
  const emitidaEm = opcoes.emitidaEm ?? hojeUtc();

  return {
    versaoSchema: VERSAO_SCHEMA_ATUAL,
    versaoConfiguracao: resultados.versaoConfiguracao,
    versaoTemplate: VERSAO_TEMPLATE,

    numero: opcoes.numero ?? `EM-${emitidaEm.slice(0, 4)}-000`,
    situacao: 'rascunho',

    cliente: v.cliente.trim(),
    projeto: v.projeto.trim(),

    emitidaEm,
    validaAte: v.validaAte,

    /*
     * A oportunidade escolhida viaja no documento com seu GUID e o GUID da conta: é o vínculo que a
     * Lambda usa, na publicação, para criar a Cotação no Dynamics (quote) ligada ao cliente e à
     * oportunidade. Quando o vendedor publica sem selecionar oportunidade, o campo fica nulo e a
     * publicação segue normalmente, apenas sem sincronizar Cotação.
     */
    oportunidade: v.oportunidade,
    sincronizacao: sincronizacaoInicial(),
    /**
     * A formalização é preenchida numa etapa própria do formulário, não aqui: um rascunho em
     * construção não tem termo de adesão, e exigir os dados de cobrança para ver a prévia
     * atrapalharia o fluxo que o vendedor usa para negociar.
     */
    formalizacao: null,

    responsavel,

    premissas: {
      trilha: v.trilha,
      planoSuporte: v.planoSuporte,
      duracaoContratoMeses: v.duracaoContratoMeses,
      ptax: v.ptax,
      ptaxOrigem: v.ptaxOrigem,
      itensCusto: temCsv(v) ? v.itensCusto : null,
      regiaoAws: v.regiaoAws,
      consumoManual: temCsv(v) ? null : v.consumoManual,
      credito: v.incluiCredito
        ? { valorUsd: v.creditoValorUsd, janelaMeses: v.creditoJanelaMeses }
        : null,
      deploy: { modalidade: v.modalidadeDeploy, custo: v.deployCusto },
      licencasBackup: v.incluiBackup
        ? {
            fileServer: v.backupFileServer,
            virtualServer: v.backupVirtualServer,
            sharepoint: v.backupSharepoint,
            vmwareHyperv: v.backupVmwareHyperv,
          }
        : null,
      safestorage: montarSafeStorage(v),
      custosAdicionais: montarCustosAdicionais(v),
      assessment: montarAssessment(v),
      // Zero é o "não incluído" do domínio, e o schema já o aceita: nada a converter além disto.
      sessoesTreinamento: v.incluiTreinamento ? v.sessoesTreinamento : 0,
    },

    resultados: {
      margemPercentual: resultados.margemPercentual,
      margemAplicada: resultados.margemAplicada,
      custoLiquidoTotal: resultados.custoLiquidoTotal,
      consumo: resultados.consumo,
      custoSuporte: resultados.custoSuporte,
      baseMensal: resultados.baseMensal,
      licencasBackup: resultados.licencasBackup,
      plataformaSafeStorage: resultados.plataformaSafeStorage,
      custosAdicionais: resultados.custosAdicionais,
      deployCustoTotal: resultados.deployCustoTotal,
      deployFaturado: resultados.deployFaturado,
      creditoAplicadoNoDeploy: resultados.creditoAplicadoNoDeploy,
      creditoResidual: resultados.creditoResidual,
      reducaoMensal: resultados.reducaoMensal,
      mensalidadeEstimada: resultados.mensalidadeEstimada,
      economiaPercentual: resultados.economiaPercentual,
      exibirEconomia: resultados.exibirEconomia,
    },

    conteudo: {
      badgeContexto: v.badgeContexto.trim(),
      promessaValor: v.promessaValor.trim(),
      subtituloHero: v.subtituloHero.trim(),
      secoes: [],
      escopoIncluido: [],
      escopoExcluido: [],
      proximosPassos: [],
      condicoesComerciais: [],
      observacoesHtml: v.observacoesHtml.trim() === '' ? null : v.observacoesHtml,
    },

    auditoria: {
      versao: 1,
      criadoPor: responsavel.sub,
      criadoEm: new Date().toISOString(),
      atualizadoPor: responsavel.sub,
      atualizadoEm: new Date().toISOString(),
      motivoAlteracao: null,
    },
  };
}

// ---------------------------------------------------------------------------
// Prévia
// ---------------------------------------------------------------------------

export interface Previa {
  /** HTML do documento, ou `null` quando não foi possível montar. */
  readonly html: string | null;
  readonly resultados: ResultadoPrecificacao | null;
  readonly documento: DocumentoProposta | null;
  /** Impedimentos para gerar a prévia, em linguagem voltada ao vendedor. */
  readonly problemas: readonly string[];
}

/**
 * Calcula e renderiza a prévia.
 *
 * Nunca lança: qualquer impedimento vira item em `problemas`. Um formulário que explode enquanto o
 * usuário digita é pior que um que explica o que falta — e é normal que os valores estejam
 * incompletos no meio do preenchimento.
 */
export function gerarPrevia(
  v: ValoresFormulario,
  responsavel: ResponsavelCongelado | null,
): Previa {
  const problemas: string[] = [];

  if (responsavel === null) {
    problemas.push('Selecione o responsável pela proposta.');
  }
  if (v.cliente.trim() === '') problemas.push('Informe o nome do cliente.');
  if (v.projeto.trim() === '') problemas.push('Informe o nome do projeto.');
  if (!temCsv(v) && v.consumoManual === null) {
    problemas.push('Importe o CSV da calculadora ou informe o consumo manualmente.');
  }

  if (problemas.length > 0 || responsavel === null) {
    return { html: null, resultados: null, documento: null, problemas };
  }

  // Preenche defaults para os campos opcionais de texto de abertura, para a prévia renderizar
  // mesmo sem o vendedor ter tocado neles. O objetivo é agilidade no preenchimento.
  const valoresComDefaults: ValoresFormulario = {
    ...v,
    /*
     * Badge de contexto do herói.
     *
     * **Não** usa a região da AWS como reserva. A região é informação técnica de precificação, e o
     * lugar dela é junto do faturamento, acima da tabela de recursos — não acima do H1, onde ficava
     * competindo com a promessa de valor. Sem preenchimento, cai para o tipo de proposta.
     */
    badgeContexto:
      v.badgeContexto.trim() !== ''
        ? v.badgeContexto
        : v.trilha === 'PS'
          ? 'Proposta comercial · Setor Público'
          : 'Proposta comercial · AWS',
    promessaValor:
      v.promessaValor.trim() !== ''
        ? v.promessaValor
        : 'a infraestrutura de nuvem que acompanha o crescimento do negócio',
    subtituloHero:
      v.subtituloHero.trim() !== ''
        ? v.subtituloHero
        : 'Proposta personalizada para impulsionar a sua jornada na nuvem AWS com a parceria eMaster.',
  };

  let resultados: ResultadoPrecificacao;
  try {
    resultados = precificar(montarEntradaPrecificacao(valoresComDefaults));
  } catch (erro) {
    return {
      html: null,
      resultados: null,
      documento: null,
      problemas: [erro instanceof Error ? erro.message : 'Não foi possível calcular o preço.'],
    };
  }

  const validado = validarDocumento(montarDocumento(valoresComDefaults, responsavel, resultados));
  if (!validado.valido || validado.dados === null) {
    return {
      html: null,
      resultados,
      documento: null,
      problemas: validado.problemas.map((p) => `${p.campo}: ${p.mensagem}`),
    };
  }

  return {
    html: renderizarProposta(validado.dados, {
      ...(v.arquiteturaDataUrl !== '' && { arquiteturaDataUrl: v.arquiteturaDataUrl }),
    }),
    resultados,
    documento: validado.dados,
    problemas: [],
  };
}
